import type { F01SafetyAnswerRow } from "@care-by-ella/contracts";

// TES-1164 (Ö-383) — what the doctor reads about type 1 diabetes and MTC/MEN
// 2: her answer, the form and date it came from, and what it means for her
// journey. Pure, so the wording is tested without a DOM.
//
// The F-01 document: "Jag är osäker → fortsätt till F-02 med en tydlig
// läkarflagga; inget automatiskt stopp." So an unsure answer is marked as
// something the doctor must assess, and a "Ja" as the stop she met.

export type SafetyTone = "stop" | "flag" | "none";

export interface SafetyLine {
  topic: string;
  question: string;
  answer: string;
  source: string;
  note: string | null;
  tone: SafetyTone;
}

const TOPIC: Record<F01SafetyAnswerRow["key"], string> = {
  type1Diabetes: "Diabetes typ 1",
  mtcMen2: "MTC/MEN 2 (patienten eller förstagradssläkting)",
};

const day = (iso: string) => iso.slice(0, 10);

function sourceOf(row: F01SafetyAnswerRow): string {
  const version =
    row.templateVersion !== null ? ` v${row.templateVersion}` : "";
  const form = row.source === "f01" ? "F-01" : "Hälsodeklarationen (F-02)";
  return `${form}${version}, ${day(row.submittedAt)}`;
}

function noteOf(row: F01SafetyAnswerRow): {
  note: string | null;
  tone: SafetyTone;
} {
  if (row.value === "unsure") {
    return {
      note: "Läkarflagga: osäkert svar, behöver bedömas individuellt",
      tone: "flag",
    };
  }
  if (row.value === "yes") {
    return row.source === "f01"
      ? { note: "Automatiskt stopp i F-01", tone: "stop" }
      : { note: "Läkarflagga från hälsodeklarationen", tone: "flag" };
  }
  return { note: null, tone: "none" };
}

/** One line per question, in question order; never asked = "Inte besvarad". */
export function f01SafetyLines(
  rows: readonly F01SafetyAnswerRow[] | undefined,
): SafetyLine[] {
  return (["type1Diabetes", "mtcMen2"] as const).map((key) => {
    const row = rows?.find((r) => r.key === key);
    if (!row) {
      return {
        topic: TOPIC[key],
        question: "",
        answer: "Inte besvarad",
        source: "",
        note: null,
        tone: "none",
      };
    }
    return {
      topic: TOPIC[key],
      question: row.question,
      answer: row.answer,
      source: sourceOf(row),
      ...noteOf(row),
    };
  });
}
